/**
 * Reschedule History service — Firestore CRUD for `rescheduleHistory`.
 *
 * One immutable record per reschedule, providing a full audit trail and the
 * source event the `onRescheduleCreated` Cloud Function reacts to (sending the
 * "class rescheduled" emails). `mentorId`/`studentId` are denormalized so reads
 * can be scoped by rules without loading the class.
 *
 *   {
 *     classId, classTitle,
 *     mentorId, studentId,
 *     oldStartAt: Timestamp, oldTimezone,
 *     newStartAt: Timestamp, newTimezone,
 *     reason,
 *     rescheduledBy: 'admin'|'mentor',
 *     rescheduledByName,
 *     slotId,                       // open slot consumed (or '')
 *     createdAt
 *   }
 */
import firestoreService, { where, orderBy, Timestamp } from './firestore.service';

const COLLECTION = 'rescheduleHistory';

function toTimestamp(value) {
  if (!value) return null;
  if (value instanceof Timestamp) return value;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
}

function sanitize(input = {}) {
  return {
    classId: input.classId || '',
    classTitle: (input.classTitle || '').trim(),
    mentorId: input.mentorId || '',
    studentId: input.studentId || '',
    oldStartAt: toTimestamp(input.oldStartAt),
    oldTimezone: input.oldTimezone || '',
    newStartAt: toTimestamp(input.newStartAt),
    newTimezone: input.newTimezone || '',
    reason: (input.reason || '').trim(),
    rescheduledBy: input.rescheduledBy || 'admin',
    rescheduledByName: (input.rescheduledByName || '').trim(),
    // True when an admin performed the reschedule (possibly on the mentor's or
    // student's behalf) — emails say "arranged by the LearnX team".
    recordedByAdmin: input.recordedByAdmin === true,
    // '' | 'student_class_limit' | 'mentor_window' — which allowance it consumed.
    countsAgainst: input.countsAgainst || '',
    slotId: input.slotId || '',
  };
}

export const rescheduleHistoryService = {
  create(data) {
    return firestoreService.addDocument(COLLECTION, sanitize(data));
  },

  /** Realtime history for one class, newest first. */
  subscribeByClass(classId, onData, onError) {
    return firestoreService.subscribeCollection(
      COLLECTION,
      [where('classId', '==', classId)],
      (rows) =>
        onData(
          [...rows].sort(
            (a, b) => (b.createdAt?.toDate?.()?.getTime?.() ?? 0) - (a.createdAt?.toDate?.()?.getTime?.() ?? 0)
          )
        ),
      onError
    );
  },

  async listByClass(classId) {
    return firestoreService.getCollection(COLLECTION, [where('classId', '==', classId)]);
  },

  /** Realtime feed of all reschedules (admin), newest first. */
  subscribeAll(onData, onError) {
    return firestoreService.subscribeCollection(
      COLLECTION,
      [orderBy('createdAt', 'desc')],
      onData,
      onError
    );
  },
};

export default rescheduleHistoryService;
